// =======================================
// DATA TYPES
// =======================================

import { FeeRowStatus } from '../InvoiceRegister/config';

/**
 * Outcome bucket for a fee entity, derived server-side by the
 * project_fee_register_entities view. Drives the status pills.
 *
 *  - Awaiting Fee: probable, no fee total yet
 *  - Pending:      probable, quoted, awaiting the client's decision
 *  - Won:          anything past probable (Complete included), plus legacy
 *                  jobs that have invoices but never had a fee record
 *  - Lost:         archived / unsuccessful
 */
export type FeeBucket = 'Awaiting Fee' | 'Pending' | 'Won' | 'Lost';

/** The two outcomes set_fee_outcome accepts. */
export type FeeOutcome = 'won' | 'lost';

/** Buckets that already carry a decision, so the row offers Change instead. */
export const DECIDED_BUCKETS: FeeBucket[] = ['Won', 'Lost'];

/**
 * Row in the Fees register list — one per ENTITY (project, or stage). For
 * multi-stage projects there's one project row + one row per stage; each
 * entity carries its own latest-fee snapshot (or 'no_fee').
 */
export interface FeeRegisterRow {
  project_id: number;
  stage_id: number | null;
  stage_code: string | null;
  stage_description: string | null;
  job_number: number | null;
  description: string;
  types: string;
  project_status: string | null;
  entity_created_at: string | null;
  has_invoices: boolean;
  fee_bucket: FeeBucket;
  workflow_status_id: number | null;
  workflow_status_name: string | null;
  client_id: number | null;
  client_name: string | null;
  fee_id: number | null;
  fee_number: string | null;
  fee_status: FeeRowStatus;
  fee_total: number;
  fee_updated_at: string | null;
  fee_items_updated_at: string | null;
  outcome_at: string | null;
}

/** Per-bucket counts returned under the RPC's `summary` key. */
export interface FeeRegisterSummary {
  awaiting_fee_count: number;
  pending_count: number;
  won_count: number;
  lost_count: number;
  total_count: number;
  /** Sum of fee_total across rows matching ALL filters, bucket included. */
  filtered_fee_total: number;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

export const DEFAULT_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Types shown by default. Shared by the `useState` initialiser and the
 * `useServerFiltering` defaults so the two can never drift apart — the page
 * must open on Design + Advisory work only.
 */
export const DEFAULT_TYPE_FILTER: string[] = ['Design', 'Advisory'];

/**
 * A pipeline view that hid last quarter's probables by default would be
 * useless, so unlike Invoices this defaults to showing everything.
 */
export const DEFAULT_DATE_RANGE_PRESET = 'allTime';

export const ALL_BUCKETS = 'All';

/** Pill order, left to right. `countKey` indexes into FeeRegisterSummary. */
export const FEE_BUCKET_PILLS: { value: string; label: string; countKey: keyof FeeRegisterSummary }[] = [
  { value: ALL_BUCKETS, label: 'All', countKey: 'total_count' },
  { value: 'Awaiting Fee', label: 'Awaiting Fee', countKey: 'awaiting_fee_count' },
  { value: 'Pending', label: 'Pending', countKey: 'pending_count' },
  { value: 'Won', label: 'Won', countKey: 'won_count' },
  { value: 'Lost', label: 'Lost', countKey: 'lost_count' },
];

export const PROJECT_TYPE_OPTIONS = [
  { id: 'Design', label: 'Design', value: 'Design' },
  { id: 'Advisory', label: 'Advisory', value: 'Advisory' },
  { id: 'RWC', label: 'RWC', value: 'RWC' },
  { id: 'RWD', label: 'RWD', value: 'RWD' },
];

export const CURRENCY_CONFIG = {
  locale: 'en-AU',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * Whole days between a timestamp and now, floored at 0. Compared on calendar
 * days rather than elapsed hours so a fee touched late yesterday reads
 * "1 day", not "0 days".
 */
export const daysSince = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const then = new Date(iso);
  if (isNaN(then.getTime())) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = startOfDay(new Date()) - startOfDay(then);
  return Math.max(0, Math.round(diff / 86400000));
};

export const formatDaysSince = (iso: string | null | undefined): string | null => {
  const days = daysSince(iso);
  if (days === null) return null;
  if (days === 0) return 'Today';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
};

export const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-AU');
};

export const formatCurrency = (amount: number | null | undefined): string => {
  return new Intl.NumberFormat(CURRENCY_CONFIG.locale, {
    style: 'currency',
    currency: CURRENCY_CONFIG.currency,
    minimumFractionDigits: CURRENCY_CONFIG.minimumFractionDigits,
    maximumFractionDigits: CURRENCY_CONFIG.maximumFractionDigits,
  }).format(amount ?? 0);
};
